/* eslint-disable @typescript-eslint/no-explicit-any */

import * as yup from 'yup';

import {
  ValidationEntry,
  FieldType,
  StringField,
  NotificationFormData,
  FormField,
  NumberField,
  BooleanField,
} from '../../../client/forms/fields';

/**
 * Utility type for extracting the validation function from client side validation entry definition.
 */
type GetFieldValidationFuncs<T extends Record<string, ValidationEntry<any>>, S = yup.AnySchema> = {
  [C in keyof T]: (schema: S, entry: T[C]) => S;
};

/**
 * Provider side validation spec.
 */
export interface FieldValidationFuncSpec extends Record<keyof typeof FieldType, any> {
  [FieldType.string]: GetFieldValidationFuncs<
    Required<Required<StringField>['validation']>,
    yup.StringSchema
  >;
  [FieldType.number]: GetFieldValidationFuncs<
    Required<Required<NumberField>['validation']>,
    yup.NumberSchema
  >;
  [FieldType.boolean]: GetFieldValidationFuncs<
    Required<Required<BooleanField>['validation']>,
    yup.BooleanSchema
  >;
}

// 0 is falsy so we need to check if arg is 0 and allow it

const noValidationArg = <T>(arg: unknown): arg is Exclude<T, undefined> =>
  arg === null || arg === undefined;

/**
 * A mapping of {@link FormField} validation from the client to a validation rule.
 */
export const FieldValidationFunctions: Required<FieldValidationFuncSpec> = {
  [FieldType.string]: {
    min: (schema: yup.StringSchema, validationEntry: ValidationEntry<number>): yup.StringSchema => {
      return schema.min(validationEntry.arg ?? 0, validationEntry.invalidMessage);
    },
    max: (schema: yup.StringSchema, validationEntry: ValidationEntry<number>): yup.StringSchema => {
      return schema.max(validationEntry.arg ?? 0, validationEntry.invalidMessage);
    },
    length: (
      schema: yup.StringSchema,
      validationEntry: ValidationEntry<number>
    ): yup.StringSchema => {
      return schema.length(validationEntry.arg ?? 0, validationEntry.invalidMessage);
    },
    required: (
      schema: yup.StringSchema,
      validationEntry: ValidationEntry<boolean>
    ): yup.StringSchema => {
      if (!validationEntry.arg) {
        return schema;
      }
      return schema.required(validationEntry.invalidMessage);
    },
    match: (
      schema: yup.StringSchema,
      validationEntry: ValidationEntry<string>
    ): yup.StringSchema => {
      if (!validationEntry.arg) {
        return schema;
      }
      const regex = new RegExp(validationEntry.arg);
      return schema.matches(regex, {
        message: validationEntry.invalidMessage,
        excludeEmptyString: true,
      });
    },
  },
  [FieldType.number]: {
    max: (schema: yup.NumberSchema, validationEntry: ValidationEntry<number>): yup.NumberSchema => {
      return noValidationArg(validationEntry.arg)
        ? schema
        : schema.max(validationEntry.arg, validationEntry.invalidMessage);
    },
    min: (schema: yup.NumberSchema, validationEntry: ValidationEntry<number>): yup.NumberSchema => {
      return noValidationArg(validationEntry.arg)
        ? schema
        : schema.min(validationEntry.arg, validationEntry.invalidMessage);
    },
    lessThan: (
      schema: yup.NumberSchema,
      validationEntry: ValidationEntry<number>
    ): yup.NumberSchema => {
      return noValidationArg(validationEntry.arg)
        ? schema
        : schema.lessThan(validationEntry.arg, validationEntry.invalidMessage);
    },
    moreThan: (
      schema: yup.NumberSchema,
      validationEntry: ValidationEntry<number>
    ): yup.NumberSchema => {
      return noValidationArg(validationEntry.arg)
        ? schema
        : schema.moreThan(validationEntry.arg, validationEntry.invalidMessage);
    },
    positive: (schema: yup.NumberSchema, validationEntry: ValidationEntry): yup.NumberSchema => {
      return schema.positive(validationEntry.invalidMessage);
    },
    negative: (schema: yup.NumberSchema, validationEntry: ValidationEntry): yup.NumberSchema => {
      return schema.negative(validationEntry.invalidMessage);
    },
    required: (schema: yup.NumberSchema, validationEntry: ValidationEntry): yup.NumberSchema => {
      return schema.required(validationEntry.invalidMessage);
    },
  },
  [FieldType.boolean]: {},
};

/**
 * A map to lookup a {@link FormField.type} schema for use in validation.
 */
const fieldTypeToSchema = {
  [FieldType.string]: yup.StringSchema,
  [FieldType.number]: yup.NumberSchema,
  [FieldType.boolean]: yup.BooleanSchema,
} as const;

function getFieldSchema(field: FormField): yup.AnySchema {
  const { type } = field;
  const Ctor = fieldTypeToSchema[type];
  // @ts-ignore
  return new Ctor();
}

/**
 * Build a single field validation schema
 */
export function buildFieldSchema(field: FormField): yup.AnySchema {
  const validationFuncMap = FieldValidationFunctions[field.type];
  if (!validationFuncMap) {
    throw new Error('Invalid field');
  }

  // We need to chain the result of validationFunc
  let schema = getFieldSchema(field);

  if (!field.validation) {
    return schema;
  }

  const validationEntries = Object.entries(field.validation);

  for (const [key, val] of validationEntries) {
    type KeyType = keyof typeof FieldValidationFunctions[typeof field.type];
    const validationFunc = validationFuncMap[key as KeyType];
    if (validationFunc) {
      let newChainSchema: yup.AnySchema | null = null;
      try {
        // TODO: Fix type
        // @ts-ignore
        newChainSchema = validationFunc(schema, val);
      } catch (error) {
        console.error(`Error parsing field ${key}`, val, field);
      }
      schema = newChainSchema ?? schema;
    }
  }

  if (field.label) {
    schema.spec.label = field.label;
  }

  return schema;
}

/**
 * Build the entire form validation schema.
 * This is used to validate the user input.
 */
export function buildFormSchema(fields: NotificationFormData): yup.AnyObjectSchema {
  const obj: Record<string, yup.AnySchema> = {};

  for (const f of fields) {
    obj[f.key] = buildFieldSchema(f);
  }

  return yup.object().shape(obj);
}

export function getFormDefaultValues(fields: NotificationFormData): Record<string, unknown> {
  const obj: Record<string, unknown> = {};
  for (const field of fields) {
    obj[field.key] = field.value ?? '';
  }
  return obj;
}

export interface ParsedFormField<T extends FormField> {
  initialValue: T['value'];
  schema: yup.AnySchema;
  fieldData: T;
  widget: T['widget'];
}

export function parseFormData(form: NotificationFormData): ParsedFormField<FormField>[] {
  form.map<ParsedFormField<FormField>>((field) => ({
    initialValue: field.value,
    fieldData: field,
    widget: field.widget,
    schema: buildFieldSchema(field),
  }));
  return [];
}
